<script lang="ts">
  import { untrack } from 'svelte';
  import PlusIcon from 'phosphor-svelte/lib/PlusIcon';
  import TrashIcon from 'phosphor-svelte/lib/TrashIcon';

  import type { PersonaTriggerView, PersonaView } from '#src/generated/protocol';

  import { useCoreClient } from '#lib/core/context.js';
  import { i18n } from '#lib/i18n.js';
  import { parsePronouns } from '#lib/personas/pronouns.js';
  import { MAX_PRONOUN_INPUT, pronounText } from '#lib/profile/pronouns.js';
  import Alert from '#lib/ui/primitives/Alert.svelte';
  import Avatar from '#lib/ui/primitives/Avatar.svelte';
  import Button from '#lib/ui/primitives/Button.svelte';
  import DialogActions from '#lib/ui/primitives/DialogActions.svelte';
  import DialogFrame from '#lib/ui/primitives/DialogFrame.svelte';
  import IconButton from '#lib/ui/primitives/IconButton.svelte';
  import Switch from '#lib/ui/primitives/Switch.svelte';
  import TextInput from '#lib/ui/primitives/TextInput.svelte';
  import { hexColorOrEmpty } from '#lib/ui/hex-color.js';
  import ColorSetting from '#lib/ui/ColorSetting.svelte';
  import { uprightJpeg } from '#lib/ui/upright-jpeg.js';

  interface Props {
    open?: boolean;
    persona: PersonaView | null;
    onSave: (persona: PersonaView, previousId: string | null) => Promise<void>;
    onOpenChange: (open: boolean) => void;
  }

  let { open = $bindable(false), persona, onSave, onOpenChange }: Props = $props();
  const core = useCoreClient();

  let name = $state('');
  let avatarUrl = $state<string | null>(null);
  let pronouns = $state('');
  let colorLight = $state('');
  let colorDark = $state('');
  let triggers = $state<(PersonaTriggerView & { id: string })[]>([]);
  let saving = $state(false);
  let error = $state<string | null>(null);

  $effect(() => {
    if (!open) return;
    untrack(() => {
      name = persona?.display_name ?? '';
      avatarUrl = persona?.avatar_url ?? null;
      pronouns = pronounText(persona?.pronouns ?? []);
      colorLight = persona?.color_on_light ?? '';
      colorDark = persona?.color_on_dark ?? '';
      triggers =
        persona?.triggers.map((trigger) => ({ ...trigger, id: crypto.randomUUID() })) ?? [];
      error = null;
    });
  });

  async function pickAvatar(event: Event & { currentTarget: HTMLInputElement }): Promise<void> {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;

    saving = true;
    error = null;
    try {
      const upright = await uprightJpeg(file);
      avatarUrl = await core.media.uploadMedia(
        upright.type || 'image/*',
        new Uint8Array(await upright.arrayBuffer())
      );
    } catch (cause) {
      console.warn('[sable personas] uploading the picture failed', cause);
      error = i18n.t('personas.saveFailed');
    } finally {
      saving = false;
    }
  }

  function addTrigger(): void {
    triggers = [
      ...triggers,
      { prefix: '', suffix: null, keep_trigger: false, id: crypto.randomUUID() },
    ];
  }

  function removeTrigger(index: number): void {
    triggers = triggers.filter((_, position) => position !== index);
  }

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const displayName = name.trim();
    if (displayName === '') {
      error = i18n.t('personas.nameRequired');
      return;
    }

    saving = true;
    error = null;
    try {
      await onSave(
        {
          id: displayName,
          display_name: displayName,
          avatar_url: avatarUrl,
          pronouns: parsePronouns(pronouns),
          color_on_light: hexColorOrEmpty(colorLight) || null,
          color_on_dark: hexColorOrEmpty(colorDark) || null,
          triggers: triggers
            .map((trigger) => ({
              prefix: trigger.prefix?.trimStart() || null,
              suffix: trigger.suffix?.trimEnd() || null,
              keep_trigger: trigger.keep_trigger,
            }))
            .filter((trigger) => trigger.prefix !== null || trigger.suffix !== null),
          pluralkit: persona?.pluralkit ?? null,
          last_used: persona?.last_used ?? null,
        },
        persona?.id ?? null
      );
      onOpenChange(false);
    } catch (cause) {
      console.warn('[sable personas] saving failed', cause);
      error = i18n.t('personas.saveFailed');
    } finally {
      saving = false;
    }
  }
</script>

<DialogFrame bind:open variant="settings" label={i18n.t('personas.editorTitle')} {onOpenChange}>
  <form class="persona-editor" onsubmit={(event) => void submit(event)}>
    <h2>{i18n.t('personas.editorTitle')}</h2>
    {#if error}<Alert variant="critical" aria-live="polite">{error}</Alert>{/if}

    <div class="identity">
      <Avatar src={avatarUrl} {name} size="large" />
      <div class="identity-actions">
        <label class="file-button btn btn-secondary btn-small">
          <input type="file" accept="image/*" onchange={(event) => void pickAvatar(event)} />
          {i18n.t('common.chooseAPicture')}
        </label>
        {#if avatarUrl}
          <Button
            variant="danger"
            size="small"
            onclick={() => {
              avatarUrl = null;
            }}
          >
            {i18n.t('personas.avatarRemove')}
          </Button>
        {/if}
      </div>
    </div>

    <label class="field">
      <span>{i18n.t('personas.name')}</span>
      <TextInput
        name="persona-name"
        bind:value={name}
        autocomplete="off"
        required
        maxlength={128}
      />
      <small>{i18n.t('personas.nameHint')}</small>
    </label>

    <label class="field">
      <span>{i18n.t('personas.pronouns')}</span>
      <TextInput
        name="persona-pronouns"
        bind:value={pronouns}
        autocomplete="off"
        placeholder={i18n.t('common.theythemEnsheher')}
        maxlength={MAX_PRONOUN_INPUT}
      />
    </label>

    <div class="colors">
      <ColorSetting
        label={i18n.t('personas.colorLight')}
        bind:value={colorLight}
        onCommit={() => {}}
        onReset={() => (colorLight = '')}
      />
      <ColorSetting
        label={i18n.t('personas.colorDark')}
        bind:value={colorDark}
        onCommit={() => {}}
        onReset={() => (colorDark = '')}
      />
    </div>

    <fieldset class="triggers">
      <legend>{i18n.t('personas.triggers')}</legend>
      <p>{i18n.t('personas.triggersHint')}</p>
      {#each triggers as trigger, index (trigger.id)}
        <div class="trigger-row">
          <label class="field">
            <span>{i18n.t('personas.triggerPrefix')}</span>
            <TextInput
              name={`persona-trigger-prefix-${String(index)}`}
              value={trigger.prefix ?? ''}
              autocomplete="off"
              oninput={(event: Event & { currentTarget: HTMLInputElement }) => {
                triggers[index] = {
                  ...trigger,
                  prefix: event.currentTarget.value,
                };
              }}
            />
          </label>
          <label class="field">
            <span>{i18n.t('personas.triggerSuffix')}</span>
            <TextInput
              name={`persona-trigger-suffix-${String(index)}`}
              value={trigger.suffix ?? ''}
              autocomplete="off"
              oninput={(event: Event & { currentTarget: HTMLInputElement }) => {
                triggers[index] = {
                  ...trigger,
                  suffix: event.currentTarget.value,
                };
              }}
            />
          </label>
          <label class="field wrap">
            <span>{i18n.t('personas.triggerKeep')}</span>
            <Switch
              label=""
              checked={trigger.keep_trigger}
              onCheckedChange={(checked: boolean) => {
                triggers[index] = { ...trigger, keep_trigger: checked };
              }}
            />
          </label>
          <div class="button">
            <IconButton
              variant="subtle"
              size="small"
              label={i18n.t('personas.triggerRemove')}
              onclick={() => {
                removeTrigger(index);
              }}
            >
              <TrashIcon />
            </IconButton>
          </div>
        </div>
      {/each}
      <Button variant="secondary" size="small" onclick={addTrigger}>
        <PlusIcon />
        {i18n.t('personas.triggerAdd')}
      </Button>
    </fieldset>

    <DialogActions>
      <Button
        variant="secondary"
        onclick={() => {
          onOpenChange(false);
        }}
      >
        {i18n.t('common.cancel')}
      </Button>
      <Button type="submit" loading={saving}>{i18n.t('common.save')}</Button>
    </DialogActions>
  </form>
</DialogFrame>

<style>
  .persona-editor {
    align-content: start;
    block-size: 100%;
    display: grid;
    gap: var(--space-400);
    overflow: hidden auto;
    overscroll-behavior: contain;
    padding: var(--space-400);
  }

  h2 {
    font-size: var(--font-size-heading);
    line-height: var(--line-height-heading);
    margin: 0;
  }

  .identity {
    align-items: center;
    display: flex;
    gap: var(--space-400);
  }

  .identity-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-300);
  }

  .file-button {
    cursor: pointer;
  }

  .file-button input {
    height: 1px;
    opacity: 0;
    position: absolute;
    width: 1px;
  }

  .field {
    display: grid;
    gap: var(--space-200);
    min-width: 0;
  }

  .field span {
    font-weight: var(--font-weight-medium);
  }

  .field small {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
  }

  .colors {
    display: grid;
    gap: var(--space-300);
  }

  .triggers {
    border: var(--border-width) solid var(--bg-container-line);
    border-radius: var(--radius);
    display: grid;
    gap: var(--space-300);
    margin: 0;
    padding: var(--space-300) var(--space-400) var(--space-400);
  }

  .triggers legend {
    font-weight: var(--font-weight-medium);
    padding: 0 var(--space-200);
  }

  .triggers p {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    margin: 0;
  }

  .trigger-row {
    align-items: center;
    column-gap: var(--space-300);
    display: grid;
    grid-template-columns: 1fr 1fr auto auto;
  }

  .trigger-row .field {
    align-items: center;
    grid-row: 1 / 3;
    grid-template-rows: subgrid;
  }

  .trigger-row .button {
    grid-row: 2 / 3;
  }

  @media (width < 34rem) {
    .trigger-row {
      grid-template-columns: 1fr 1fr auto;
      grid-template-rows: auto auto var(--space-300) auto;
    }

    .trigger-row .field.wrap {
      grid-column: 1 / 4;
      grid-row: 4 / 5;
      grid-template-columns: 1fr auto;
    }

    .triggers {
      gap: var(--space-600);
    }
  }
</style>
